import React, {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import SimpleCard from "connectui/component/display/card/SimpleCard";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Text from "connectui/component/display/Text";
import Container from "connectui/component/layout/Container";

import EmptyButtonBase from "connectui/component/input/button/base/EmptyButtonBase";
import ButtonIconText from "connectui/component/input/button/ButtonIconText";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import ModalBase from "connectui/component/modal/ModalBase";
import ListComponent from "connectui/component/layout/list/ListComponent";
import {getDashboardMessageAnalytics} from "redux/actions/dashboardActions";
import DashboardEmptyStateCard from "./DashboardEmptyStateCard";
import {useTranslation} from "react-i18next";
import Tooltip from "connectui/component/display/Tooltip";
import Divider from "../../../../connectui/component/display/Divider";

const MessageCountDisplay = ({totalMessages, loading, theme}) => (
  <FlexContainer alignItems="flex-end" gap={theme.spacing(2)}>
    <Text
      variant="displaySM"
      weight="semibold"
      skeleton={{enabled: loading, width: "80px", height: "32px"}}
    >
      {loading ? "" : totalMessages.toLocaleString()}
    </Text>
    <Text
      variant="textMD"
      weight={"semibold"}
      sx={{paddingBottom: theme.spacing(0.8)}}
    >
      Messages
    </Text>
  </FlexContainer>
);

const MessageTypeListItem = ({messageType, theme}) => (
  <FlexContainer
    alignItems="center"
    justifyContent="space-between"
    sx={{
      padding: theme.spacing(2, 0),
    }}
  >
    <Container
      sx={{
        flex: 1,
        minWidth: 0,
        marginRight: theme.spacing(2),
      }}
    >
      <Tooltip
        tooltipContent={messageType.type}
        sx={{
          tooltip: {
            maxWidth: "300px",
            whiteSpace: "normal",
            wordBreak: "break-word",
          },
          trigger: {
            display: "block",
            width: "100%",
          },
        }}
      >
        <Text
          variant="textSM"
          weight="medium"
          sx={{
            textAlign: "left",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {messageType.type}
        </Text>
      </Tooltip>
    </Container>
    <FlexContainer
      alignItems="center"
      gap={theme.spacing(3)}
      sx={{flexShrink: 0}}
    >
      <Text variant="textSM" color="textSecondary" sx={{textAlign: "right"}}>
        {messageType.count} messages
      </Text>
      <Text
        variant="textSM"
        weight="semibold"
        color="primary"
        sx={{paddingRight: theme.spacing(2)}}
      >
        {messageType.percentage}%
      </Text>
    </FlexContainer>
  </FlexContainer>
);

const MessageInsightsList = ({
  messageTypes,
  loading,
  hasData,
  theme,
  maxItems = null,
}) => {
  if (loading) {
    return (
      <Container sx={{width: "100%"}}>
        {Array.from({length: 4}).map((_, index) => (
          <Container
            key={index}
            skeleton={{enabled: true, width: "100%", height: "40px"}}
            sx={{marginBottom: theme.spacing(2)}}
          />
        ))}
      </Container>
    );
  }

  if (!hasData) {
    return (
      <DashboardEmptyStateCard
        text={"No message insights available"}
        icon={ICONS["no-messages-white"]}
        height="200px"
      />
    );
  }

  const displayedTypes = maxItems
    ? messageTypes.slice(0, maxItems)
    : messageTypes;

  return (
    <ListComponent
      gap="0px"
      listStyles={{
        width: "100%",
      }}
    >
      {displayedTypes.map((messageType, index) => (
        <MessageTypeListItem
          key={index}
          messageType={messageType}
          theme={theme}
        />
      ))}
    </ListComponent>
  );
};

const MessageInsightsCard = ({timeframe, listingGroups = null}) => {
  const {theme} = useTheme();
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!timeframe?.startDate || !timeframe?.endDate) return;

    setLoading(true);
    dispatch(
      getDashboardMessageAnalytics({
        start_date: timeframe.startDate,
        end_date: timeframe.endDate,
        listing_group_ids: listingGroups,
        stat: "question_type",
        onSuccess: (response) => {
          setData(response);
          setLoading(false);
        },
        onError: () => setLoading(false),
      }),
    );
  }, [timeframe?.startDate, timeframe?.endDate, listingGroups, dispatch]);

  const hasData = data?.question_type?.length > 0;
  const totalMessages = hasData
    ? data.question_type.reduce(
        (total, item) => total + (item.num_of_messages || 0),
        0,
      )
    : 0;

  const messageTypes = hasData
    ? data.question_type
        .map((item) => ({
          type: item.question_type || "Unknown",
          count: item.num_of_messages || 0,
          percentage:
            totalMessages > 0
              ? (((item.num_of_messages || 0) / totalMessages) * 100).toFixed(1)
              : "0.0",
        }))
        .sort((a, b) => b.count - a.count)
    : [];

  const renderBottomBar = () => (
    <FlexContainer
      flexDirection="column"
      justifyContent="center"
      alignItems={"center"}
      sx={{width: "100%"}}
    >
      <Divider />
      <ButtonIconText
        buttonBase={EmptyButtonBase}
        label={"View More"}
        onClick={() => setModalOpen(true)}
        color={theme.colors.primary}
        as="div"
        sx={{
          padding: theme.spacing(1, 2),
          color: theme.colors.primary,
          fontSize: theme.typography.textSM.fontSize,
          fontWeight: theme.typography.textSM.medium.fontWeight,
          "&:hover": {
            backgroundColor: theme.colors.primary50,
          },
        }}
        icon={
          <SvgIcon
            component={ICONS["arrow-up-right"]}
            sx={{
              width: "20px",
              height: "20px",
              marginRight: theme.spacing(0.5),
            }}
          />
        }
      />
    </FlexContainer>
  );

  const renderCardTitle = () => (
    <FlexContainer alignItems="center" gap={theme.spacing(1)}>
      <Text variant={"textMD"} color={"textSecondary"} weight={"medium"}>
        {t("dashboard.messaging-insights.title")}
      </Text>
      <Tooltip tooltipContent={t("dashboard.messaging-insights.help")}>
        <Container sx={{cursor: "help"}}>
          <SvgIcon
            component={ICONS["help-circle"]}
            sx={{
              width: "14px",
              height: "14px",
              color: theme.colors.gray400,
            }}
          />
        </Container>
      </Tooltip>
    </FlexContainer>
  );

  return (
    <>
      <SimpleCard
        customContent={
          <FlexContainer
            width="100%"
            flexDirection="column"
            gap={theme.spacing(3)}
          >
            <FlexContainer
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              {renderCardTitle()}
            </FlexContainer>

            <MessageCountDisplay
              totalMessages={totalMessages}
              loading={loading}
              theme={theme}
            />

            <MessageInsightsList
              messageTypes={messageTypes}
              loading={loading}
              hasData={hasData}
              theme={theme}
              maxItems={4}
            />

            {renderBottomBar()}
          </FlexContainer>
        }
        sx={{
          height: "100%",
          width: "100%",
          padding: theme.spacing(5, 5, 3, 5),
          backgroundColor: theme.colors.white,
        }}
      />

      <ModalBase
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        sx={{
          padding: theme.spacing(6),
          maxWidth: "800px",
          width: "90%",
          maxHeight: "80vh",
        }}
      >
        <FlexContainer
          flexDirection="column"
          gap={theme.spacing(3)}
          sx={{height: "100%"}}
        >
          <FlexContainer
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            {renderCardTitle()}
            <SvgIcon
              component={ICONS.x}
              onClick={() => setModalOpen(false)}
              sx={{
                cursor: "pointer",
                width: "14px",
                height: "14px",
                color: theme.colors.gray500,
              }}
            />
          </FlexContainer>

          <MessageCountDisplay
            totalMessages={totalMessages}
            loading={loading}
            theme={theme}
          />

          <Container sx={{height: "400px", width: "100%", overflow: "auto"}}>
            <MessageInsightsList
              messageTypes={messageTypes}
              loading={loading}
              hasData={hasData}
              theme={theme}
            />
          </Container>
        </FlexContainer>
      </ModalBase>
    </>
  );
};

export default MessageInsightsCard;
